import {Injectable} from '@angular/core';
import {Observable, Subject, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {PAGE, WORD_API_URL, WORD_DETAIL_API_URL, WORD_LINK_API_URL} from '../../defines/protocol';
import {IsEmptyString, IsEmptyVar} from '../../defines/common.func';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from '../../models/word.model';
import {ListLoader, ListLoader_NextList} from '../../interfaces/list-loader';
import {ApiService} from '../api/api.service';
import {SearchParam} from '../../defines/client.const';
import {NormalizeSearchKeyword} from '../../defines/search-keyword.func';


@Injectable({
  providedIn: 'root'
})
// TODO: ListLoader<Word> 를 ListLoader<SimpleWord> 로 변경해야 함.
export class WordService implements ListLoader<SimpleWord> {
  protected urlApi = WORD_API_URL;

  protected totalCount: number;
  protected pageSize: number;
  protected listWord: Array<SimpleWord>;
  protected mapWord: Map<string, SimpleWord>;
  protected nextUrl: string;
  private load$: Observable<ListLoader_NextList<SimpleWord>> = null;
  private loadDetail$: Observable<Word> = null;
  private loadLink$: Observable<Word> = null;

  constructor(
    protected api: ApiService,
  ) {
  }

  protected getReadCount(): number {
    return this.pageSize;
  }

  /******************************************************************************
   * ListLoader<SimpleWord> 구현부
   *****************************************************************************/
  public getList(): Array<SimpleWord> {
    return this.listWord;
  }
  public getCurCount(): number {
    return IsEmptyVar(this.listWord) ? null : this.listWord.length;
  }
  public getTotalCount(): number {
    return IsEmptyVar(this.totalCount) ? null : this.totalCount;
  }
  public IsComplete(): boolean {
    return this.totalCount === 0 ||
      (!IsEmptyVar(this.listWord) && !IsEmptyVar(this.totalCount) && this.listWord.length === this.totalCount);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(id: string): SimpleWord {
    return this.mapWord.get(id) ?? null;
  }

  public ResetList(param: SearchParam = null): void {
    this.nextUrl = null;
    this.listWord = null;
    this.mapWord = null;
    this.totalCount = null;
    this.pageSize = null;

    const queryParams = new URLSearchParams();

    if (param) {
      if ('index' in param) {
        queryParams.set('index', param.index);
      } else if ('keyword' in param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (!IsEmptyString(keyword)) {
          queryParams.set('search', keyword);
        }
      } else if ('hanja' in param) {
        queryParams.set('hanja', param.hanja);
        // 음(hangul)은 한자(hanja)가 있을 때만 함께 전달하는 선택 옵션이다.
        if (param.hangul) queryParams.set('hangul', param.hangul);
      }

      if ('level' in param) queryParams.set('level', param.level.toString());
      if ('start' in param) queryParams.set('start', param.start);
      if ('saja' in param) queryParams.set('saja', param.saja.toString());
    }

    this.nextUrl = this.urlApi + (queryParams.toString() ? `?${queryParams.toString()}` : '');
  }

  /**
   * return true if first page
   */
  public GetNextList(): Observable<ListLoader_NextList<SimpleWord>> {
    if (IsEmptyString(this.nextUrl)) {
      return throwError({error: 'No more pages in WordList'});
    }

    if (this.load$) {
      return this.load$;
    }

    return this.load$ = this.api.Get(this.nextUrl).pipe(
      catchError(err => {
        if ('status' in err && err.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      } ),
      map((data: PAGE) => {
        console.log('getWordList.api.Get data =', data);

        let first = false;
        if (this.totalCount === null) {
          this.totalCount = data.count;
          this.pageSize = data.page_size;
          this.listWord = new Array<SimpleWord>();
          this.mapWord = new Map<string, SimpleWord>();
          first = true;
        }
        else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data);
        }

        // 뒤에서 useCount만큼 잘라낸다.
        const results = data.results.slice(- this.getReadCount());

        const list = new Array<SimpleWord>();
        results.forEach(value => {
            const word = new SimpleWord(value);
            list.push(word);
            this.listWord.push(word);
            this.mapWord.set(word.id, word);
          });

          this.nextUrl = data.next;

          return {list, first};
        })
    );
  }


  /******************************************************************************
   * 상세 단어어 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  // tslint:disable-next-line:variable-name
  public GetDetail(word_id: string): Observable<Word> {
    const url = WORD_DETAIL_API_URL + word_id + '/';
    console.log('WordService::GetDetail() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      map(value => {
        const out = new Word(value);
        console.log('WordService::GetDetail data = ', value, out);
        return out;
      }),
      finalize(() => this.loadDetail$ = null),
    );
  }


  /******************************************************************************
   * 링크용 단순 단어어 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  // tslint:disable-next-line:variable-name
  public GetLinkInfo(word_id: string): Observable<Word> {
    const url = WORD_LINK_API_URL + word_id + '/';
    console.log('WordService::GetLinkInfo() url =', url);

    return this.loadLink$ = this.api.Get(url).pipe(
      map(value => {
        const out = new Word(value);
        console.log('WordService::GetLinkInfo data = ', value, out);
        return out;
      }),
      finalize(() => this.loadLink$ = null)
    );
  }
}
